import { useEffect, useState } from "react";
import { Star, Award, BadgeCheck } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Stars, ReplyNote } from "@/components/Bits";
import { fileUrl } from "@/lib/api";
import { useT } from "@/lib/i18n";

export const TopRatedBadge = ({ testid = "top-rated-badge" }) => {
  const { t } = useT();
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gradient-to-r from-amber-400 to-orange-500 px-2.5 py-1 text-[11px] font-extrabold text-white shadow-sm" data-testid={testid}>
      <Award className="h-3.5 w-3.5" />{t("top_rated")}
    </span>
  );
};

export const RatingPill = ({ b, testid = "rating-pill" }) => {
  const { t } = useT();
  if (!b.rating_count) return <span className="text-xs font-medium text-slate-400" data-testid={testid}>{t("no_reviews")}</span>;
  return (
    <span className="inline-flex items-center gap-1 rounded-lg border border-amber-200/70 bg-amber-50 px-2 py-0.5" data-testid={testid}>
      <Star className="h-4 w-4 fill-amber-400 text-amber-500" />
      <b className="text-sm font-black text-amber-900">{b.rating_avg.toFixed(1)}</b>
      <span className="text-xs font-semibold text-slate-500">({b.rating_count})</span>
    </span>
  );
};

const Bar = ({ n, count, total, color }) => {
  const [w, setW] = useState(0);
  useEffect(() => { const id = requestAnimationFrame(() => setW(total ? (count / total) * 100 : 0)); return () => { cancelAnimationFrame(id); }; }, [count, total]);
  return (
    <div className="flex items-center gap-2 text-xs font-semibold text-slate-600" data-testid={`rating-bar-${n}`}>
      <span className="w-3">{n}</span><Star className="h-3 w-3 fill-amber-400 text-amber-400" />
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full transition-[width] duration-700 ease-out" style={{ width: `${w}%`, backgroundColor: color }} />
      </div>
      <span className="w-6 text-right">{count}</span>
    </div>
  );
};

export const RatingHero = ({ b, color = "#F59E0B" }) => {
  const { t } = useT();
  const bd = b.rating_breakdown || {};
  if (!b.rating_count) {
    return (
      <div className="flex items-center gap-4" data-testid="rating-hero">
        <Stars value={0} size="h-6 w-6" />
        <p className="text-sm font-semibold text-slate-600" data-testid="rating-hero-empty">{t("first_review")}</p>
      </div>
    );
  }
  return (
    <div className="flex items-center gap-6" data-testid="rating-hero">
      <div className="text-center">
        <p className="font-shop text-6xl font-extrabold leading-none text-ink" data-testid="rating-hero-avg">{b.rating_count ? b.rating_avg.toFixed(1) : "–"}</p>
        <div className="mt-2"><Stars value={b.rating_avg} size="h-4 w-4" /></div>
        <p className="mt-1 text-xs font-semibold text-slate-500" data-testid="rating-hero-count">{t("based_on", { n: b.rating_count || 0 })}</p>
      </div>
      <div className="flex-1 space-y-1.5" data-testid="rating-breakdown">
        {[5, 4, 3, 2, 1].map((n) => <Bar key={n} n={n} count={bd[n] || 0} total={b.rating_count || 0} color={color} />)}
      </div>
    </div>
  );
};

export const ReviewCard = ({ r, accent = "#C2410C" }) => {
  const { t, fmtDate } = useT();
  const [open, setOpen] = useState(null);
  return (
    <article className="rounded-2xl border border-line bg-white p-5 transition-shadow duration-300 hover:shadow-md" data-testid={`review-${r.id}`}>
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 flex-shrink-0 place-items-center rounded-full font-bold text-white" style={{ backgroundColor: accent }}>{(r.user_name || "?")[0].toUpperCase()}</span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <b className="text-ink">{r.user_name}</b>
            <span className="inline-flex items-center gap-1 rounded-md border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-bold text-emerald-800" data-testid="verified-enquiry-tag">
              <BadgeCheck className="h-3 w-3" />{t("verified_enquiry")}
            </span>
          </div>
          <div className="mt-0.5 flex items-center gap-2"><Stars value={r.rating} size="h-3.5 w-3.5" /><span className="text-xs text-slate-400">{fmtDate(r.updated_at || r.created_at)}</span></div>
        </div>
      </div>
      {r.comment && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed text-slate-700">{r.comment}</p>}
      {r.photos?.length > 0 && (
        <div className="mt-3 flex gap-2" data-testid="review-photos">
          {r.photos.map((p) => (
            <button key={p} onClick={() => setOpen(p)} className="overflow-hidden rounded-lg" data-testid="review-photo-thumb">
              <img src={fileUrl(p, 400)} alt="" loading="lazy" decoding="async" className="h-20 w-20 object-cover transition-transform duration-300 hover:scale-110" />
            </button>
          ))}
        </div>
      )}
      <ReplyNote reply={r.reply} />
      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent className="max-w-3xl border-0 bg-transparent p-0 shadow-none" data-testid="review-photo-lightbox">
          <DialogTitle className="sr-only">{r.user_name}</DialogTitle>
          {open && <img src={fileUrl(open, 1600)} alt="" className="max-h-[85vh] w-full rounded-2xl object-contain" />}
        </DialogContent>
      </Dialog>
    </article>
  );
};
